import React, { useState } from 'react';
import { Calendar, Clock, Trash2, RefreshCw, FileText, TrendingUp, AlertCircle } from 'lucide-react';
import { ReportSelectorProps } from '../../types/components';
import { REPORT_GENERATION_TRIGGERS } from '../../types/problemDiagnosis';
import './ReportSelector.css';

const ReportSelector: React.FC<ReportSelectorProps> = ({
  reports,
  selectedReportId,
  onReportSelect,
  onDeleteReport,
  className = ''
}) => {
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);

  const handleDeleteReport = async (reportId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // 防止触发选择事件
    
    if (window.confirm('确定要删除这个诊断报告吗？此操作无法撤销。')) {
      setDeletingReportId(reportId);
      try {
        await onDeleteReport(reportId);
      } catch (error) {
        console.error('删除报告失败:', error);
      } finally {
        setDeletingReportId(null);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes}分钟前`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}小时前`;
    } else if (diffInHours < 24 * 7) {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}天前`;
    } else {
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getGenerationTriggerIcon = (trigger: string) => {
    switch (trigger) {
      case 'initial':
        return <FileText className="w-3 h-3 text-blue-500" />;
      case 'regenerated':
        return <RefreshCw className="w-3 h-3 text-green-500" />;
      case 'edited':
        return <TrendingUp className="w-3 h-3 text-orange-500" />;
      default:
        return <FileText className="w-3 h-3 text-gray-500" />;
    }
  };

  const getConfidenceColor = (score?: number) => {
    if (!score) return 'text-gray-400';
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (score?: number) => {
    if (!score) return '未知';
    if (score >= 0.8) return '高';
    if (score >= 0.6) return '中';
    return '低';
  };

  if (reports.length === 0) {
    return (
      <div className={`bg-gray-50 rounded-lg p-8 text-center ${className}`}>
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">暂无诊断报告</h3>
        <p className="text-gray-600">
          完成五大原理分析后，可以生成AI诊断报告
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          诊断报告 ({reports.length})
        </h3>
        {reports.length > 1 && (
          <span className="text-sm text-gray-500">
            点击选择要查看的报告
          </span>
        )}
      </div>

      <div className="space-y-2">
        {reports.map((report) => {
          const isSelected = report.id === selectedReportId;
          const isDeleting = deletingReportId === report.id;
          
          return (
            <div
              key={report.id}
              onClick={() => onReportSelect(report.id)}
              className={`
                relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                ${isSelected 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }
                ${isDeleting ? 'opacity-50 pointer-events-none' : ''}
              `}
            >
              {/* 版本标识 */}
              {report.is_latest && (
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    最新
                  </span>
                </div>
              )}

              {/* 报告标题 */}
              <div className="flex items-start justify-between mb-3">
                <h4 className={`font-medium text-sm leading-tight pr-16 ${
                  isSelected ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {report.title}
                </h4>
              </div>

              {/* 报告元信息 */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <div className="flex items-center space-x-3">
                  {/* 生成时间 */}
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(report.created_at)}</span>
                  </div>
                  
                  {/* 版本号 */}
                  <div className="flex items-center space-x-1">
                    {getGenerationTriggerIcon(report.generation_trigger)}
                    <span>v{report.version}</span>
                  </div>
                </div>

                {/* 置信度 */}
                {report.confidence_score && (
                  <div className={`flex items-center space-x-1 ${getConfidenceColor(report.confidence_score)}`}>
                    <TrendingUp className="w-3 h-3" />
                    <span>
                      置信度: {getConfidenceLabel(report.confidence_score)} ({Math.round(report.confidence_score * 100)}%)
                    </span>
                  </div>
                )}
              </div>

              {/* 报告摘要 */}
              {report.summary && (
                <p className="text-xs text-gray-600 leading-relaxed mb-3 line-clamp-2">
                  {report.summary}
                </p>
              )}

              {/* 生成触发原因 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  {getGenerationTriggerIcon(report.generation_trigger)}
                  <span>{REPORT_GENERATION_TRIGGERS[report.generation_trigger as keyof typeof REPORT_GENERATION_TRIGGERS]}</span>
                </div>

                {/* 删除按钮 */}
                {!report.is_latest && (
                  <button
                    onClick={(e) => handleDeleteReport(report.id, e)}
                    disabled={isDeleting}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="删除报告"
                  >
                    {isDeleting ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                  </button>
                )}
              </div>

              {/* 选中指示器 */}
              {isSelected && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-lg"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* 报告管理提示 */}
      {reports.length > 3 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">报告管理提示</p>
              <p className="mt-1">
                您有多个诊断报告版本。建议定期清理旧版本以保持整洁。
                最新版本的报告无法删除。
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSelector;